/**
 * OnboardingTour — guia visual pra usuário no primeiro login.
 *
 * Posicionamento inteligente do tooltip (computeTooltipPosition):
 *  - Sidebar tall (altura > 60% vh) → tooltip à direita
 *  - Topbar wide (largura > 60% vw, top < 100) → tooltip abaixo, alinhado à direita
 *  - Padrão → abaixo / acima / centro com clamp final
 *
 * Conteúdo extra (campo `extra` em STEPS):
 *  - Algumas etapas têm visual customizado (ex: kbd hint pra ⌘K / Ctrl+K)
 *
 * Disparos:
 *  1. Automático no primeiro acesso (após 800ms)
 *  2. Forçado via query param `?startTour=1` (botão "Ver tour novamente")
 */
"use client";

import { ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/router";


const SEEN_KEY = "miller.onboarding.v1";
const TOOLTIP_W = 420;
const TOOLTIP_H = 280;  // ↑ subido pra acomodar kbd hint
const GAP = 16;


interface Step {
  target?:    string;
  titleKey:   string;
  bodyKey:    string;
  /** Conteúdo extra renderizado abaixo do body (visual custom). */
  extra?:     "kbd-cmdk";
}

const STEPS: Step[] = [
  {
    titleKey: "onboarding.welcome.title",
    bodyKey:  "onboarding.welcome.body",
  },
  {
    target:   "[data-tour='sidebar']",
    titleKey: "onboarding.sidebar.title",
    bodyKey:  "onboarding.sidebar.body",
  },
  {
    target:   "[data-tour='cmd-k-hint']",
    titleKey: "onboarding.cmdk.title",
    bodyKey:  "onboarding.cmdk.body",
    extra:    "kbd-cmdk",
  },
  {
    target:   "[data-tour='topbar-perfil']",
    titleKey: "onboarding.profile.title",
    bodyKey:  "onboarding.profile.body",
  },
  {
    titleKey: "onboarding.done.title",
    bodyKey:  "onboarding.done.body",
  },
];


// ── Kbd visual ───────────────────────────────────────────────────────────────
function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[2rem] h-8 px-2 rounded-md bg-app-soft border border-app-strong text-app-1 text-sm font-mono font-semibold shadow-sm">
      {children}
    </kbd>
  );
}

function KbdHint({ isMac }: { isMac: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="my-4 grid grid-cols-2 gap-3">
      {/* Mac */}
      <div className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border ${isMac ? "border-accent bg-accent/5" : "border-app-subtle bg-app-soft/50"}`}>
        <div className="flex items-center gap-1.5">
          <Kbd>⌘</Kbd>
          <span className="text-app-3 text-xs">+</span>
          <Kbd>K</Kbd>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-app-3 font-semibold">
          {t("onboarding.cmdk.mac", "Mac")}
        </span>
      </div>
      {/* Windows / Linux */}
      <div className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border ${!isMac ? "border-accent bg-accent/5" : "border-app-subtle bg-app-soft/50"}`}>
        <div className="flex items-center gap-1.5">
          <Kbd>Ctrl</Kbd>
          <span className="text-app-3 text-xs">+</span>
          <Kbd>K</Kbd>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-app-3 font-semibold">
          {t("onboarding.cmdk.win", "Windows / Linux")}
        </span>
      </div>
    </div>
  );
}


function computeTooltipPosition(targetRect: DOMRect | null, vw: number, vh: number): React.CSSProperties {
  if (!targetRect) {
    return {
      position:  "fixed",
      left:      "50%",
      top:       "50%",
      transform: "translate(-50%, -50%)",
      maxWidth:  `${TOOLTIP_W}px`,
      width:     "calc(100% - 32px)",
    };
  }

  let left = 0;
  let top = 0;

  const isVerticalColumn = targetRect.height > vh * 0.6 && targetRect.width < vw * 0.3;
  const isTopbarBand     = targetRect.width > vw * 0.6 && targetRect.top < 100;

  if (isVerticalColumn) {
    left = Math.min(targetRect.right + GAP, vw - TOOLTIP_W - GAP);
    top  = Math.max(GAP, Math.min(vh / 2 - TOOLTIP_H / 2, vh - TOOLTIP_H - GAP));
    if (left + TOOLTIP_W > vw - GAP) {
      left = Math.max(GAP, targetRect.left - GAP - TOOLTIP_W);
    }
  } else if (isTopbarBand) {
    top  = targetRect.bottom + GAP;
    left = Math.max(GAP, Math.min(targetRect.right - TOOLTIP_W, vw - TOOLTIP_W - GAP));
  } else {
    const fitsBelow = targetRect.bottom + GAP + TOOLTIP_H <= vh - GAP;
    const fitsAbove = targetRect.top - GAP - TOOLTIP_H >= GAP;

    if (fitsBelow) {
      top = targetRect.bottom + GAP;
    } else if (fitsAbove) {
      top = targetRect.top - GAP - TOOLTIP_H;
    } else {
      top = Math.max(GAP, (vh - TOOLTIP_H) / 2);
    }

    left = Math.max(GAP, Math.min(targetRect.left, vw - TOOLTIP_W - GAP));
  }

  top  = Math.max(GAP, Math.min(top,  vh - TOOLTIP_H - GAP));
  left = Math.max(GAP, Math.min(left, vw - TOOLTIP_W - GAP));

  return {
    position: "fixed",
    left:     `${left}px`,
    top:      `${top}px`,
    width:    `${TOOLTIP_W}px`,
    maxWidth: "calc(100vw - 32px)",
  };
}


export function OnboardingTour() {
  const { t } = useTranslation();
  const router = useRouter();
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [vp, setVp] = useState({ w: 0, h: 0 });
  const [isMac, setIsMac] = useState(false);

  useEffect(() => {
    if (typeof navigator === "undefined") return;
    setIsMac(/Mac|iPhone|iPad|iPod/.test(navigator.platform) || /Mac/.test(navigator.userAgent));
  }, []);

  useEffect(() => {
    if (!router.isReady) return;
    const forced = router.query.startTour === "1";

    if (forced) {
      try { localStorage.removeItem(SEEN_KEY); } catch {}
      setStepIdx(0);
      setActive(true);
      const { startTour, ...rest } = router.query;
      router.replace(
        { pathname: router.pathname, query: rest },
        undefined,
        { shallow: true },
      );
      return;
    }

    try {
      const seen = localStorage.getItem(SEEN_KEY);
      if (!seen) {
        const timer = setTimeout(() => setActive(true), 800);
        return () => clearTimeout(timer);
      }
    } catch {}
  }, [router.isReady, router.query.startTour]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    if (!active) return;
    const step = STEPS[stepIdx];
    if (!step.target) {
      setTargetRect(null);
      return;
    }
    const update = () => {
      const el = document.querySelector(step.target!);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setTargetRect(rect);
        } else {
          setTargetRect(null);
        }
      } else {
        setTargetRect(null);
      }
    };
    const t1 = setTimeout(update, 50);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      clearTimeout(t1);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [active, stepIdx]);

  function next() {
    if (stepIdx < STEPS.length - 1) {
      setStepIdx(stepIdx + 1);
    } else {
      finish();
    }
  }

  function back() {
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  }

  function finish() {
    try { localStorage.setItem(SEEN_KEY, "1"); } catch {}
    setActive(false);
  }

  if (!active || typeof window === "undefined" || vp.w === 0) return null;

  const step = STEPS[stepIdx];
  const tooltipStyle = computeTooltipPosition(targetRect, vp.w, vp.h);

  return createPortal(
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-[400] pointer-events-auto">
        {targetRect ? (
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <mask id="onboarding-mask">
                <rect width="100%" height="100%" fill="white" />
                <rect
                  x={targetRect.left - 8}
                  y={targetRect.top - 8}
                  width={targetRect.width + 16}
                  height={targetRect.height + 16}
                  rx="12"
                  fill="black"
                />
              </mask>
            </defs>
            <rect width="100%" height="100%" fill="rgba(0,0,0,0.65)" mask="url(#onboarding-mask)" />
            <rect
              x={targetRect.left - 8}
              y={targetRect.top - 8}
              width={targetRect.width + 16}
              height={targetRect.height + 16}
              rx="12"
              fill="none"
              stroke="rgba(255,255,255,0.6)"
              strokeWidth="2"
            />
          </svg>
        ) : (
          <div className="absolute inset-0 bg-black/65" />
        )}
      </div>

      {/* Tooltip */}
      <div
        className="z-[401] bg-app-surface rounded-2xl border border-app shadow-2xl p-5"
        style={tooltipStyle}
      >
        <div className="flex items-start justify-between mb-3">
          <p className="text-[10px] font-semibold text-app-3 uppercase tracking-widest">
            {t("onboarding.step", "Passo")} {stepIdx + 1} / {STEPS.length}
          </p>
          <button
            onClick={finish}
            className="text-app-3 hover:text-app-1 -mt-1 -mr-1 p-1"
            aria-label={t("common.close", "Fechar")}
            title={t("common.close", "Fechar")}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <h3 className="font-serif text-xl font-semibold text-app-1 mb-2">
          {t(step.titleKey)}
        </h3>
        <p className="text-sm text-app-2 leading-relaxed">
          {t(step.bodyKey, { shortcut: isMac ? "⌘K" : "Ctrl+K" })}{/* // R24G_SHORTCUT_DYNAMIC */}
        </p>

        {/* Conteúdo extra do step (ex: visual de kbd) */}
        {step.extra === "kbd-cmdk" && <KbdHint isMac={isMac} />}

        <div className="flex items-center justify-between gap-2 mt-5">
          {stepIdx > 0 ? (
            <button onClick={back} className="btn-link text-xs">
              {t("common.back", "Voltar")}
            </button>
          ) : (
            <button onClick={finish} className="btn-link text-xs">
              {t("onboarding.skip", "Pular")}
            </button>
          )}
          <button onClick={next} className="btn-primary btn-sm">
            {stepIdx === STEPS.length - 1
              ? t("onboarding.finish", "Concluir")
              : t("common.next", "Próximo")}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
